import { createReducer } from "@reduxjs/toolkit";
import { typePagination } from "../system/constants";
import { logoutAC, resetReducers } from "../system/systemActionCreators";
import {
  dSalesArchiveSetDataAC,
  dSalesArchiveSetFiltersAC,
  dSalesArchiveClearFiltersAC,
  dSalesArchiveSetPageNumberAC,
  dSalesArchiveSetPaginationAC,
  dSalesArchiveSetDetailItemAC,
  dSalesArchiveSetItemsForPageAC,
  dSalesArchiveSetDetailItemFromRefundAC,
} from "./actionCreators";

export type typeArchiveSalesFilters = {
  salePublicId?: string | null;
  receiptNumber?: string | null;
  date?: {
    endDate: string | null;
    startDate: string | null;
  } | null;
};

export type IdArchiveSalesState = {
  data: IArchiveSale[];
  pagination: typePagination;
  filters: typeArchiveSalesFilters;
  detailItem: IArchiveSale | undefined;
};

export interface IArchiveSale {
  pos: Pos;
  id: string;
  fmId: string;
  date: string;
  store: Store;
  order: Order;
  paid: boolean;
  userId: string;
  status: string;
  amount: number;
  orderId: string;
  userName: string;
  refunds: Refund[];
  refunded: boolean;
  cashierId: string;
  fiscalSign: string;
  saleNumber: number;
  payments: Payment[];
  description: string;
  salePublicId: string;
  externalApiId: string;
  receiptNumber: string;
  appletVersion: string;
  zreportNumber: string;
  discountAmount: number;
  productList: ProductList[];
  servicePaymentAmount: number;
}

export interface Order {
  id: string;
  store: Store;
  phone: string;
  saleId: string;
  status: string;
  number: number;
  storeId: string;
  address: string;
  comment: string;
  createdBy: string;
  courierId: string;
  updatedBy: string;
  discount: Discount;
  externalId: string;
  collectorId: string;
  dateCreated: string;
  dateUpdated: string;
  payments: Payment[];
  customerName: string;
  servicePayment: ServicePayment;
  orderedProducts: OrderedProduct[];
  rejectedProducts: RejectedProduct[];
  acceptedProducts: AcceptedProduct[];
}

export interface AcceptedProduct {
  id: string;
  tax: number;
  psid: string;
  price: number;
  amount: number;
  marked: boolean;
  unitCode: number;
  productId: string;
  barcodes: string[];
  productName: string;
  packageCode: string;
  customTotal: number;
  commissionTIN: string;
  markedLabels: string[];
  discountAmount: number;
  commissionPINFL: string;
  productOwnerType: string;
}

export interface Discount {
  value: number;
  isInPercents: boolean;
}

export interface OrderedProduct {
  id: string;
  tax: number;
  psid: string;
  price: number;
  amount: number;
  marked: boolean;
  unitCode: number;
  productId: string;
  barcodes: string[];
  packageCode: string;
  customTotal: number;
  productName: string;
  commissionTIN: string;
  markedLabels: string[];
  discountAmount: number;
  commissionPINFL: string;
  productOwnerType: string;
}

export interface Payment {
  id: string;
  date: string;
  method: string;
  amount: number;
  paymentType: string;
  paymentAmount: number;
  paymentInfo: PaymentInfo;
  transactionNumber: number;
  paymentTransactionValue: number;
  paymentInfoDto?: {
    rrn: string | null;
    paymentSystemName?: string;
  };
}

export interface PaymentInfo {
  rrn: string;
  stan: string;
  authCode: string;
  merchantId: string;
  terminalId: string;
  responseCode: string;
  paymentSystemName: string;
  transactionNumber: number;
}

export interface RejectedProduct {
  id: string;
  tax: number;
  psid: string;
  price: number;
  amount: number;
  marked: boolean;
  unitCode: number;
  productId: string;
  barcodes: string[];
  packageCode: string;
  productName: string;
  customTotal: number;
  commissionTIN: string;
  markedLabels: string[];
  discountAmount: number;
  commissionPINFL: string;
  productOwnerType: string;
}

export interface ServicePayment {
  value: number;
  isInPercents: boolean;
}

export interface Store {
  id: string;
  name: string;
  email: string;
  manager: string;
  address: string;
  deleted: boolean;
  externalId: string;
  industryId: string;
  description: string;
  phoneNumber: string;
  interfaceType: string;
  externalSystemCode: string;
  managerPhoneNumber: string;
}

export interface Pos {
  id: string;
  store: Store;
  active: boolean;
  blocked: boolean;
  updatedBy: string;
  posNumber: number;
  createdBy: string;
  externalId: string;
  erpEnabled: boolean;
  fiscalCardId: string;
  dateEdited: DateEdited;
  fullPrepayment: boolean;
  posHardwareModel: string;
  dateCreated: DateCreated;
  externalSystemCode: string;
  posHardwareSerialNumber: string;
  fastPaymentMethodEnabled: boolean;
  certificatePaymentEnabled: boolean;
}

export interface DateEdited {
  date: number;
  time: number;
  year: number;
  nanos: number;
  hours: number;
  month: number;
  minutes: number;
  seconds: number;
}

export interface DateCreated {
  date: number;
  time: number;
  year: number;
  nanos: number;
  hours: number;
  month: number;
  minutes: number;
  seconds: number;
}

export interface ProductList {
  id: string;
  tax: number;
  psid: string;
  price: number;
  amount: number;
  marked: boolean;
  unitCode: number;
  productId: string;
  barcodes: string[];
  packageCode: string;
  customTotal: number;
  productName: string;
  commissionTIN: string;
  markedLabels: string[];
  discountAmount: number;
  commissionPINFL: string;
  productOwnerType: string;
  product?: {
    id: string;
    vat: number;
    psid: string;
    name: string;
    units: string;
  };
}

export interface Refund {
  id: string;
  fmId: string;
  date: string;
  store: Store;
  userId: string;
  amount: number;
  orderId: string;
  userName: string;
  cashierId: string;
  fiscalSign: string;
  payments: Payment[];
  zreportNumber: string;
  appletVersion: string;
  receiptNumber: string;
  productList: ProductList[];
}

export const initialState: IdArchiveSalesState = {
  pagination: {
    pageNumber: 0,
    itemsPerPage: 14,
  },
  data: [],
  detailItem: undefined,
  filters: {
    date: null,
    salePublicId: null,
    receiptNumber: null,
  },
};

export const DSalesArchiveReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(dSalesArchiveClearFiltersAC, (state) => {
      state.filters.date = null;
    })
    .addCase(dSalesArchiveSetDataAC, (state, action) => {
      state.data = action.payload.dataList;
    })
    .addCase(dSalesArchiveSetFiltersAC, (state, action) => {
      state.filters = { ...state.filters, ...action.payload };

      state.pagination.pageNumber = 0;
    })
    .addCase(dSalesArchiveSetPaginationAC, (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload.pagination };
    })
    .addCase(dSalesArchiveSetPageNumberAC, (state, action) => {
      state.pagination.pageNumber = action.payload.pageNumber;
    })
    .addCase(dSalesArchiveSetDetailItemAC, (state, action) => {
      state.detailItem = state.data.find((item) => item.id === action.payload);
    })
    .addCase(dSalesArchiveSetItemsForPageAC, (state, action) => {
      state.pagination = {
        ...state.pagination,
        pageNumber: 0,
        itemsPerPage: action.payload.itemsForPage,
      };
    })
    .addCase(dSalesArchiveSetDetailItemFromRefundAC, (state, action) => {
      state.detailItem = action.payload;
    })
    .addCase(logoutAC, (state) => {
      state.data = initialState.data;
      state.filters = initialState.filters;
      state.pagination = initialState.pagination;
      state.detailItem = initialState.detailItem;
    })
    .addCase(resetReducers, (state, action) => {
      if (action.payload !== "sales") {
        state.data = initialState.data;
        state.filters = initialState.filters;
        state.detailItem = initialState.detailItem;
        state.pagination = initialState.pagination;
      }
    });
});
